import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Stack } from '@mui/material';
import { Inventory, People, ShoppingCart, Receipt } from '@mui/icons-material';
import { productService } from '../services/product.service';
import { customerService } from '../services/customer.service';
import { salesService } from '../services/sales.service';
import { employeeService } from '../services/employee.service';
import { useLanguage } from '../i18n/LanguageContext';

interface StatCardProps { title: string; value: number; icon: React.ReactNode; color: string; }

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => (
  <Paper sx={{ p: 3, flex: 1, minWidth: 200 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box>
        <Typography color="text.secondary" variant="body2">{title}</Typography>
        <Typography variant="h4" sx={{ mt: 1 }}>{value}</Typography>
      </Box>
      <Box sx={{ bgcolor: color, borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>{icon}</Box>
    </Box>
  </Paper>
);

const Dashboard: React.FC = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState({ products: 0, customers: 0, orders: 0, employees: 0 });

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const [products, customers, orders, employees] = await Promise.all([
        productService.getAll().catch(() => []), customerService.getAll().catch(() => []),
        salesService.getAllOrders().catch(() => []), employeeService.getAll().catch(() => []),
      ]);
      setStats({ products: products.length, customers: customers.length, orders: orders.length, employees: employees.length });
    } catch (error) { console.error('Error loading dashboard stats:', error); }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>{t('dashboard.title')}</Typography>
      <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
        <StatCard title={t('dashboard.total_products')} value={stats.products} icon={<Inventory />} color="#1976d2" />
        <StatCard title={t('dashboard.total_customers')} value={stats.customers} icon={<People />} color="#2e7d32" />
        <StatCard title={t('sales_orders.title')} value={stats.orders} icon={<ShoppingCart />} color="#ed6c02" />
        <StatCard title={t('dashboard.total_employees')} value={stats.employees} icon={<Receipt />} color="#9c27b0" />
      </Stack>
    </Box>
  );
};

export default Dashboard;
